/**
 * Inference detail page.
 *
 * Renders the full inference record fetched from
 * GET /v1/inference/<uid>:
 *   * Hero card: deployment + segment + status + latency + timestamp
 *   * Big chart: per-step p10/p50/p90 with tooltip + axes
 *   * Step-by-step table: every horizon step with all quantiles +
 *     band width, plus a CSV export
 *   * Request / response metadata + segment metadata cards
 *   * "Export JSON" downloads the entire raw record
 *
 * Routes: /inferences/<uid>
 */
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, CircularProgress, Alert, Chip,
  Stack, Divider, IconButton, Tooltip,
} from '@mui/material';
import { InferencesIcon } from '../global/CustomIcons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from '../../utils/datetime';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import StatusChip from '../common/StatusChip';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';
import { Surface } from '../dashboard/components/DashboardPrimitives';
import { CMButtonOutlinedSmall } from '../../containers/styledComponents';
import {
  FanChart, ScoreBars, ForecastValuesTable, predictionToForecast,
} from '../common/ForecastCharts';
import { useSignalUnits } from '../../utils/units';
import {
  fetchRequest as fetchInferenceDetail,
  clearState as clearInferenceDetail,
} from '../../redux/actions/inferences/detail';

// --- helpers --------------------------------------------------------------

const formatTs = (s) => {
  if (!s) return '—';
  try { return format(new Date(s), 'PPpp'); } catch { return s; }
};

const flatten = (arr) => {
  // Foundation models hand us nested arrays (`[[v1, v2, ...]]` for
  // single-label, `[[[v1], [v2]]]` for multi-label). Segment inference
  // hands us a single number (single-step, single-label dict value)
  // or a flat array (multi-step). Normalize all of those to a flat
  // 1D array of scalars so chart/table code can iterate uniformly.
  if (arr === null || arr === undefined) return [];
  if (typeof arr === 'number') return [arr];
  if (!Array.isArray(arr)) return [];
  if (Array.isArray(arr[0]) && Array.isArray(arr[0][0])) {
    return arr[0].map((step) => (Array.isArray(step) ? step[0] : step));
  }
  if (Array.isArray(arr[0])) {
    return arr.map((step) => (Array.isArray(step) ? step[0] : step));
  }
  return arr;
};

/**
 * Normalize the many shapes `inference.response.predictions` can take
 * into the canonical `Array<{label, point, quantiles?}>` the rest of
 * this component is written against.
 *
 * Backend variants we have to handle:
 *
 *   1. Foundation models (Chronos / PredictFM / etc.) - already
 *      canonical:
 *        [{ label: 'close', point: [...], quantiles: {0.1: [...], ...} }]
 *
 *   2. Segment inference on shared/ModelMesh deployments - dict shape
 *      keyed by label:
 *        { close: [v1, v2, v3, ...] }   // multi-step
 *        { close: 12.34 }               // single-step
 *      (see core/src/app/inferences/inference.py::map_predictions_to_labels)
 *
 *   3. Raw KServe / Triton path (very early failure path) - just an
 *      array of numbers with no label info:
 *        [v1, v2, v3, ...]
 *
 *   4. Empty / null / error response - return `[]` so the UI shows
 *      nothing instead of crashing with `predictions.map is not a function`.
 *
 * The `point` field is always returned as an array (even for
 * single-step), so downstream code can use `flatten(pred.point)` and
 * not worry about scalar-vs-array branches.
 */
const normalizePredictions = (raw) => {
  if (raw === null || raw === undefined) return [];

  // Variant 1: already canonical
  if (
    Array.isArray(raw)
    && raw.length > 0
    && typeof raw[0] === 'object'
    && raw[0] !== null
    && 'label' in raw[0]
  ) {
    return raw.map((p) => ({
      label: p.label,
      point: Array.isArray(p.point) ? p.point : [p.point],
      quantiles: p.quantiles,
    }));
  }

  // Variant 3: raw numeric array (no label info available - bucket
  // under 'output' so the chart still renders something useful).
  if (Array.isArray(raw)) {
    return [{
      label: 'output',
      point: raw.map((v) => (Array.isArray(v) ? v[0] : v)),
    }];
  }

  // Variant 2: dict keyed by label
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([label, value]) => ({
      label,
      point: Array.isArray(value) ? value : [value],
    }));
  }

  return [];
};

const triggerDownload = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// --- detail page ----------------------------------------------------------

const InferenceDetailPage = ({
  detail, isLoading, error,
  loadDetail, clearDetail,
}) => {
  const { uid } = useParams();
  const navigate = useNavigate();
  // Measurement units per signal key — labels ARE signal keys here.
  const unitOf = useSignalUnits();

  useEffect(() => {
    loadDetail(uid);
    return () => clearDetail();
  }, [uid, loadDetail, clearDetail]);

  const inference = detail?.inference;
  const intervalSec = inference?.segment?.interval || 60;

  // Historical context comes inline on the inference detail response
  // (BE field ``inference.history`` keyed by label name; see
  // ``GET /v1/inference/<id>``). Eliminates the second round-trip
  // and the prior "forecast renders, then re-renders once history
  // loads" flash. Convert the column-oriented BE shape
  // ({timestamps: [...], values: [...]}) into the {x, y}[] tuples
  // ForecastChart consumes.
  const history = useMemo(() => {
    const raw = inference?.history || {};
    const out = {};
    for (const [label, series] of Object.entries(raw)) {
      const ts = series?.timestamps || [];
      const vs = series?.values || [];
      const pts = [];
      for (let i = 0; i < ts.length; i++) {
        const v = vs[i];
        if (v !== null && v !== undefined) {
          pts.push({ x: ts[i], y: v });
        }
      }
      out[label] = pts;
    }
    return out;
  }, [inference]);
  const historyError = null;

  const exportJson = () => {
    if (!inference) return;
    const filename = `inference_${inference.uid}.json`;
    triggerDownload(filename, 'application/json',
      JSON.stringify(inference, null, 2));
  };

  const exportCsv = (prediction, label) => {
    if (!prediction) return;
    // Mirror StepsTable's column-shape logic: deterministic models
    // (no quantiles) export `step,timestamp,value`; probabilistic
    // models export `step,timestamp,p10,p50,p90,band_width`. Same
    // explicit length checks because empty arrays are truthy in JS -
    // the original `||` fallback was a no-op.
    const point = flatten(prediction.point);
    const q10raw = flatten(prediction.quantiles?.['0.1']);
    const q50fromQuantiles = flatten(prediction.quantiles?.['0.5']);
    const q90raw = flatten(prediction.quantiles?.['0.9']);
    const q50 = q50fromQuantiles.length > 0 ? q50fromQuantiles : point;
    const q10 = q10raw.length > 0 ? q10raw : [];
    const q90 = q90raw.length > 0 ? q90raw : [];

    const len = q50.length;
    if (!len) return;

    // Recompute step timestamps the same way StepsTable / ForecastChart
    // do, so the exported CSV is row-aligned with what's on screen.
    // ISO 8601 UTC timestamps in the CSV - clients can parse them
    // unambiguously (no locale formatting).
    const intervalMs = (intervalSec || 60) * 1000;
    const labelHistory = history[label] || [];
    const haveLabelHist = labelHistory.length > 0;
    const anchorMs = haveLabelHist
      ? new Date(labelHistory[labelHistory.length - 1].x).getTime()
      : (inference?.timestamp ? new Date(inference.timestamp).getTime() : Date.now());

    const isProbabilistic = q10.length === len && q90.length === len;
    const rows = isProbabilistic
      ? [['step', 'timestamp', 'p10', 'p50', 'p90', 'band_width']]
      : [['step', 'timestamp', 'value']];
    for (let i = 0; i < len; i++) {
      const m = q50[i];
      const ts = new Date(anchorMs + (i + 1) * intervalMs).toISOString();
      if (isProbabilistic) {
        const a = q10[i];
        const b = q90[i];
        const w = (typeof a === 'number' && typeof b === 'number') ? Math.abs(b - a) : '';
        rows.push([i + 1, ts, a ?? '', m ?? '', b ?? '', w]);
      } else {
        rows.push([i + 1, ts, m ?? '']);
      }
    }
    const csv = rows.map((r) => r.join(',')).join('\n');
    const safeLabel = String(label || 'forecast').replace(/[^a-z0-9_-]+/gi, '_');
    triggerDownload(
      `inference_${inference.uid}_${safeLabel}.csv`,
      'text/csv',
      csv,
    );
  };

  if (isLoading && !inference) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <PageBreadcrumbs items={[
          { text: 'Inferences', icon: <InferencesIcon />, link: '/inference' },
          { text: uid },
        ]} />
        <Alert severity="error">Failed to load inference: {String(error)}</Alert>
      </Box>
    );
  }
  if (!inference) return null;

  const ok = inference.response?.success !== false && !inference.response?.error;
  // `predictions` can come back as several different shapes depending
  // on which serving path produced this inference (foundation model,
  // segment-inference on ModelMesh, raw KServe array, or just null).
  // normalizePredictions() collapses all of them to the canonical
  // Array<{label, point, quantiles?}> shape this component is written
  // against - without this, `predictions.map(...)` blew up on every
  // segment-inference detail page (object-shaped response).
  const predictions = normalizePredictions(inference.response?.predictions);
  const segMeta = inference.segment_metadata || {};
  const reqMeta = inference.request || {};
  const perf = inference.performance || {};
  // Held-out quality of the model behind this inference (enriched by the
  // GET /inference/<id> endpoint from the model's training report).
  const modelMetrics = inference.model_metrics || {};
  const hasModelMetrics = modelMetrics.accuracy_score != null
    || modelMetrics.skill_score != null;
  const isClassMetrics = modelMetrics.score_kind === 'classification';
  // Goal-type-aware rendering. Classification goals (anomaly / trend) emit a
  // probability, not a price — rendering them on the forecast chart's price
  // axis turns a correct "0% anomaly" into a line crashing to zero. The
  // serving layer tags these via serving_mode (see discovery.studio).
  const servingMode = segMeta.serving_mode
    || reqMeta.parameters?.serving_mode
    || 'forecast';
  const isScoreMode = servingMode === 'anomaly_score'
    || servingMode === 'trend_direction';

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <PageBreadcrumbs items={[
          { text: 'Inferences', icon: <InferencesIcon />, link: '/inference' },
          { text: inference.deployment_label || inference.deployment_id || uid },
        ]} />
        <Stack direction="row" spacing={1}>
          <Tooltip title="Back to list">
            <IconButton onClick={() => navigate('/inference')}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => loadDetail(uid)}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <CMButtonOutlinedSmall
            startIcon={<DownloadIcon />}
            onClick={exportJson}
          >
            Export JSON
          </CMButtonOutlinedSmall>
        </Stack>
      </Box>

      {/* Hero card */}
      <Surface sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
              DEPLOYMENT
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {inference.deployment_label || inference.deployment_id || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {inference.deployment_id}
            </Typography>
            {inference.deployment?.foundation_adapter_id && (
              <Box sx={{ mt: 1 }}>
                <Chip size="small" label="LoRA adapter attached" color="primary" variant="outlined" />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
              SEGMENT
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {inference.segment_name || segMeta.segment_name || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {segMeta.segment_id}
            </Typography>
            {(segMeta.labels_used || []).length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {segMeta.labels_used.map((l) => (
                  <Chip key={l} size="small" label={l} variant="outlined" />
                ))}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
              STATUS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <StatusChip
                status={ok ? 'success' : 'failed'}
                label={ok ? 'Success' : 'Failed'}
                size="md"
              />
              <Typography variant="body2">
                {Math.round(perf.latency_ms ?? 0)}ms
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {formatTs(inference.timestamp)}
            </Typography>
          </Grid>
        </Grid>

        {hasModelMetrics && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                MODEL QUALITY
              </Typography>
              <Typography variant="caption" color="text.disabled">
                held-out (back-test){modelMetrics.model_name ? ` · ${modelMetrics.model_name}` : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <AccuracyInfoTooltip
                value={modelMetrics.accuracy_score != null ? Number(modelMetrics.accuracy_score) : null}
                scoreKind={isClassMetrics ? 'classification' : undefined}
                note={modelMetrics.model_name ? `Model: ${modelMetrics.model_name}` : null}
              >
                <Box sx={{ cursor: 'help' }}>
                  <Typography variant="caption" color="text.secondary">Accuracy</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {modelMetrics.accuracy_score != null
                      ? `${Number(modelMetrics.accuracy_score).toFixed(1)}%` : '—'}
                  </Typography>
                </Box>
              </AccuracyInfoTooltip>
              <Tooltip
                title={isClassMetrics
                  ? 'Skill over a coin-flip baseline: max(0, 2·(AUC−0.5))×100. 0% = no better than chance.'
                  : 'Skill over the naive baseline (carry-forward): R²-style value-add. 0% = no better than predicting "no change"; this is the honest read of whether the model is actually learning structure.'}
                arrow
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">Skill</Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={modelMetrics.skill_score != null
                      && Number(modelMetrics.skill_score) <= 0
                      ? 'warning.main' : 'text.primary'}
                  >
                    {modelMetrics.skill_score != null
                      ? `${Number(modelMetrics.skill_score).toFixed(1)}%` : '—'}
                  </Typography>
                </Box>
              </Tooltip>
            </Stack>
          </>
        )}

        {!ok && inference.response?.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {String(inference.response.error)}
          </Alert>
        )}
      </Surface>

      {/* Per-label forecasts (chart + table) */}
      {predictions.map((pred, idx) => {
        const labelHist = history[pred.label] || [];
        return (
          <Surface key={`${pred.label}-${idx}`} sx={{ p: 3, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {pred.label}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {isScoreMode && (
                  <Chip
                    size="small"
                    color="secondary"
                    label={servingMode === 'anomaly_score' ? 'Anomaly detection' : 'Trend direction'}
                  />
                )}
                {labelHist.length > 0 && !isScoreMode && (
                  <Chip
                    size="small"
                    label={`History: ${labelHist.length} pts`}
                    variant="outlined"
                  />
                )}
                <Chip
                  size="small"
                  label={`Horizon: ${flatten(pred.point).length || segMeta.horizon || '—'} steps`}
                  variant="outlined"
                />
                {!isScoreMode && (
                  <Chip
                    size="small"
                    label={`Context: ${segMeta.context_length ?? reqMeta.parameters?.context_length ?? '—'}`}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Stack>
            {(() => {
              const fc = predictionToForecast({
                prediction: pred,
                history: labelHist,
                intervalSec,
                inferenceTs: inference.timestamp,
                servingMode,
                unit: unitOf(pred.label),
              });
              return isScoreMode ? (
                <>
                  <ScoreBars forecast={fc} anomaly={servingMode === 'anomaly_score'} />
                  <ForecastValuesTable
                    forecast={fc}
                    mode="score"
                    anomaly={servingMode === 'anomaly_score'}
                    onExportCsv={() => exportCsv(pred, pred.label)}
                  />
                </>
              ) : (
                <>
                  {historyError && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      Could not load segment history: {String(historyError)}.
                      The chart will render predictions only.
                    </Alert>
                  )}
                  <FanChart forecast={fc} />
                  <Divider sx={{ my: 2 }} />
                  <ForecastValuesTable
                    forecast={fc}
                    mode="fan"
                    onExportCsv={() => exportCsv(pred, pred.label)}
                  />
                </>
              );
            })()}
          </Surface>
        );
      })}

      {/* Metadata cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Surface sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Request
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <KvList rows={[
              ['Inference type', inference.inference_type],
              ['Input shape', JSON.stringify(reqMeta.input_shape || [])],
              ['Input size', reqMeta.input_size],
              ['Horizon', reqMeta.parameters?.horizon ?? segMeta.horizon],
              ['Context length',
                reqMeta.parameters?.context_length ?? segMeta.context_length],
              ['Labels',
                (reqMeta.parameters?.labels || segMeta.labels_used || []).join(', ') || '—'],
            ]} />
          </Surface>
        </Grid>
        <Grid item xs={12} md={6}>
          <Surface sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Response
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <KvList rows={[
              ['Success', String(inference.response?.success ?? '—')],
              ['Output shape', JSON.stringify(inference.response?.output_shape || [])],
              ['Latency', perf.latency_ms ? `${Math.round(perf.latency_ms)} ms` : '—'],
              ['Status code', perf.status_code ?? '—'],
              ['User agent', inference.metadata?.user_agent
                ? truncate(inference.metadata.user_agent, 60) : '—'],
              ['IP', inference.metadata?.ip_address ?? '—'],
            ]} />
          </Surface>
        </Grid>
      </Grid>
    </Box>
  );
};

const KvList = ({ rows }) => (
  <Stack spacing={1}>
    {rows.map(([k, v]) => (
      <Stack key={k} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
          {k}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right',
          fontFamily: typeof v === 'string' && v.length > 30 ? 'monospace' : undefined,
          wordBreak: 'break-all',
        }}>
          {v ?? '—'}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

const truncate = (s, n) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const mapStateToProps = (state) => ({
  detail:    state.inferences_detail.response,
  isLoading: state.inferences_detail.isLoading,
  error:     state.inferences_detail.error,
});

const mapDispatchToProps = (dispatch) => ({
  loadDetail:   (uid) => dispatch(fetchInferenceDetail(uid)),
  clearDetail:  ()    => dispatch(clearInferenceDetail()),
});

export default connect(mapStateToProps, mapDispatchToProps)(InferenceDetailPage);
